import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

const OPS_PAGE_SIZE = 50;

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'medium' });
}

export default function AdminOperationsLogView() {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const [qEmployee, setQEmployee] = useState('');
  const [actionKey, setActionKey] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [qCargoRol, setQCargoRol] = useState('');

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const inFlightRef = useRef(false);
  const hasMoreRef = useRef(false);
  const rowsRef = useRef([]);
  const filterRef = useRef({});

  const buildFilters = useCallback((offset) => {
    const f = filterRef.current;
    return {
      limit: OPS_PAGE_SIZE,
      offset,
      q_employee: f.qEmployee.trim() || undefined,
      action_key: f.actionKey || undefined,
      date_from: f.dateFrom || undefined,
      date_to: f.dateTo || undefined,
      q_cargo_rol: f.qCargoRol.trim() || undefined,
    };
  }, []);

  const fetchPage = useCallback(async (offset, { append = false } = {}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (append) setLoadingMore(true);
    else setLoading(true);
    if (!append) setError('');
    try {
      const res = await api.admin.operationsLog.list(buildFilters(offset));
      if (!res?.ok) throw new Error(res?.error || 'No fue posible cargar el registro');
      const page = Array.isArray(res.data) ? res.data : [];
      if (Array.isArray(res.catalog) && res.catalog.length && !append) {
        setCatalog(res.catalog);
      }
      setTotal(Number(res.total || 0));
      setHasMore(!!res.has_more);
      hasMoreRef.current = !!res.has_more;
      setRows((prev) => {
        const next = append ? [...prev, ...page] : page;
        rowsRef.current = next;
        return next;
      });
    } catch (e) {
      if (!append) {
        setRows([]);
        rowsRef.current = [];
        setTotal(0);
        setHasMore(false);
        hasMoreRef.current = false;
      }
      setError(e?.message || 'Error de red al cargar el registro');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildFilters]);

  const applyFilters = useCallback(() => {
    filterRef.current = { qEmployee, actionKey, dateFrom, dateTo, qCargoRol };
    fetchPage(0, { append: false });
  }, [actionKey, dateFrom, dateTo, fetchPage, qCargoRol, qEmployee]);

  useEffect(() => {
    filterRef.current = { qEmployee, actionKey, dateFrom, dateTo, qCargoRol };
    fetchPage(0, { append: false });
  }, []);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      if (inFlightRef.current || !hasMoreRef.current) return;
      fetchPage(rowsRef.current.length, { append: true });
    }, { root, rootMargin: '80px', threshold: 0 });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage, rows.length]);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Registro de operaciones</h3>
      <p className="text-sm text-gray-600 mb-4">
        Operaciones de negocio y descargas de usuarios (retención 120 días). Orden: más reciente primero.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Empleado</span>
          <input
            type="text"
            className="w-full border rounded px-2 py-1.5"
            value={qEmployee}
            onChange={(e) => setQEmployee(e.target.value)}
            placeholder="Nombre o correo"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Acción</span>
          <select
            className="w-full border rounded px-2 py-1.5"
            value={actionKey}
            onChange={(e) => setActionKey(e.target.value)}
          >
            <option value="">Todas</option>
            {catalog.map((item) => (
              <option key={item.action_key} value={item.action_key}>{item.action_label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Desde</span>
          <input type="date" className="w-full border rounded px-2 py-1.5" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Hasta</span>
          <input type="date" className="w-full border rounded px-2 py-1.5" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Cargo o rol</span>
          <input
            type="text"
            className="w-full border rounded px-2 py-1.5"
            value={qCargoRol}
            onChange={(e) => setQCargoRol(e.target.value)}
            placeholder="Cargo, rol sistema u operativo"
          />
        </label>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          className="px-3 py-1.5 rounded bg-teal-700 text-white text-sm hover:bg-teal-800"
          onClick={applyFilters}
          disabled={loading}
        >
          Aplicar filtros
        </button>
        <span className="text-sm text-gray-500">{total > 0 ? `${total} coincidencias` : 'Sin coincidencias'}</span>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <div ref={scrollRef} className="max-h-[min(70vh,640px)] overflow-y-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Nombre</th>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Cargo</th>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Rol</th>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Fecha y hora</th>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Operación</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{row.actor_name || '—'}</div>
                  {row.actor_email && <div className="text-xs text-gray-500">{row.actor_email}</div>}
                </td>
                <td className="px-3 py-2 text-gray-800">{row.cargo || '—'}</td>
                <td className="px-3 py-2 text-gray-800">
                  <div><span className="text-xs text-gray-500">Sistema:</span> {row.rol_sistema || '—'}</div>
                  {row.rol_operativo && (
                    <div><span className="text-xs text-gray-500">Operativo:</span> {row.rol_operativo}</div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-800">{formatDateTime(row.created_at)}</td>
                <td className="px-3 py-2 text-gray-800">
                  <div className="font-medium">{row.action_label}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{row.summary}</div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && !error && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-500">No hay operaciones para los filtros seleccionados.</td>
              </tr>
            )}
          </tbody>
        </table>
        <div ref={sentinelRef} className="h-8 flex items-center justify-center text-xs text-gray-500">
          {loadingMore ? 'Cargando más…' : hasMore ? '' : rows.length > 0 ? 'Fin del listado' : ''}
        </div>
      </div>
      {loading && rows.length === 0 && (
        <p className="text-sm text-gray-500 mt-2">Cargando registro…</p>
      )}
    </div>
  );
}
